import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, LayoutGrid } from "lucide-react";
import { addMonths, addWeeks, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, format, isSameMonth, isToday } from "date-fns";
import { useLang } from "@/lib/i18n";
import { fmtDate, STAFF_DOT, fmtTime } from "@/lib/format";

const pillCls = (e) => {
  if (e.status === "cancelled") return "border-neutral-200 text-neutral-400 line-through";
  if (e.status === "draft") return "border-dashed border-white/15 text-neutral-500";
  return { full: "border-emerald-200 bg-emerald-50 text-emerald-800", partial: "border-amber-200 bg-amber-50 text-amber-800", vacant: "border-red-200 bg-red-50 text-red-800" }[e.stats.staffing_status];
};

const EventPill = ({ e, onOpen, big }) => {
  const { t } = useLang();
  return (
  <button
    data-testid={`calendar-event-${e.id}`}
    onClick={() => onOpen(e.id)}
    className={`press w-full text-left border rounded-md px-1.5 py-1 text-[11px] leading-tight truncate hover:border-[#C5A059] ${pillCls(e)} ${big ? "py-2 px-2.5 whitespace-normal" : ""}`}
    title={e.name}
  >
    <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${STAFF_DOT[e.stats.staffing_status]}`} />
    {big && <span className="mono text-[10px] text-neutral-500 mr-1">{fmtTime(e.start_time, t)}</span>}
    {e.name}
    {big && <span className="block mono text-[10px] mt-1 opacity-80">{e.stats.filled}/{e.stats.required}</span>}
  </button>
  );
};

export const EventCalendar = ({ events }) => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const open = (id) => navigate(`/admin/events/${id}`);

  const byDate = useMemo(() => {
    const m = {};
    (events || []).forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [events]);

  const days = view === "month"
    ? eachDayOfInterval({ start: startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 }), end: endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 }) })
    : eachDayOfInterval({ start: startOfWeek(cursor, { weekStartsOn: 1 }), end: endOfWeek(cursor, { weekStartsOn: 1 }) });
  const shift = (n) => setCursor(view === "month" ? addMonths(cursor, n) : addWeeks(cursor, n));
  const title = view === "month" ? fmtDate(format(cursor, "yyyy-MM-dd"), lang, "MMMM yyyy") : `${fmtDate(format(days[0], "yyyy-MM-dd"), lang, "d MMM")} – ${fmtDate(format(days[6], "yyyy-MM-dd"), lang, "d MMM yyyy")}`;

  return (
    <div className="card-lux" data-testid="event-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-black/[0.06]">
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev" onClick={() => shift(-1)} className="press p-1.5 rounded-full border border-black/10 hover:border-[#C5A059]"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="calendar-next" onClick={() => shift(1)} className="press p-1.5 rounded-full border border-black/10 hover:border-[#C5A059]"><ChevronRight className="w-4 h-4" /></button>
          <h3 className="font-display text-2xl capitalize ml-2" data-testid="calendar-title">{title}</h3>
          <button data-testid="calendar-today" onClick={() => setCursor(new Date())} className="press ml-2 text-xs text-neutral-500 hover:text-black underline underline-offset-4">{t("dash.today")}</button>
          <button data-testid="calendar-day-board" onClick={() => navigate(`/admin/day/${format(isSameMonth(cursor, new Date()) || view === "week" ? cursor : startOfMonth(cursor), "yyyy-MM-dd")}`)} className="press ml-2 inline-flex items-center gap-1.5 rounded-full border border-[#C5A059]/50 px-3 py-1.5 text-xs text-[#D9B872] hover:bg-[#C5A059]/10"><LayoutGrid className="w-3.5 h-3.5" />{t("day.open")}</button>
        </div>
        <div className="inline-flex rounded-full border border-white/10 p-0.5 text-xs">
          {["month", "week"].map((v) => (
            <button key={v} data-testid={`calendar-view-${v}`} onClick={() => setView(v)} className={`press rounded-full px-3 py-1.5 ${view === v ? "bg-[#C5A059] text-[#0A0A0C] font-semibold" : "text-neutral-500 hover:text-black"}`}>{t(`dash.${v}`)}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="grid grid-cols-7 min-w-[700px]">
          {days.slice(0, 7).map((d) => (
            <div key={`h${d}`} className="eyebrow px-2 py-2 border-b border-black/[0.06]">{fmtDate(format(d, "yyyy-MM-dd"), lang, "EEE").replace(".", "")}</div>
          ))}
          {days.map((d) => {
            const key = format(d, "yyyy-MM-dd");
            const list = byDate[key] || [];
            return (
              <div key={key} data-testid={`calendar-day-${key}`}
                className={`group border-b border-r border-black/[0.05] p-1.5 ${view === "month" ? "min-h-[104px]" : "min-h-[260px]"} ${view === "month" && !isSameMonth(d, cursor) ? "bg-neutral-50/70 opacity-50" : "bg-transparent"}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className={`mono text-[11px] ${isToday(d) ? "inline-grid place-items-center w-5 h-5 bg-[#C5A059] text-[#0A0A0C] rounded-full" : "text-neutral-400"}`}>{format(d, "d")}</div>
                  {list.length > 0 && <button data-testid={`calendar-day-board-${key}`} title={t("day.open")} onClick={() => navigate(`/admin/day/${key}`)} className="press p-0.5 text-neutral-500 hover:text-[#D9B872]"><LayoutGrid className="w-3 h-3" /></button>}
                </div>
                <div className="space-y-1">
                  {list.map((e) => <EventPill key={e.id} e={e} onOpen={open} big={view === "week"} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
